;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { cn } from '@qogita/ui/utils/cn'
import { Anchor } from '@qogita/ui/v2/anchor'
import clsx from 'clsx'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { AnchorHTMLAttributes, forwardRef, ReactNode } from 'react'

/**
 * Returns a continuous array of numbers from start to end
 * @param start starting number
 * @param end ending number
 * @returns number[]
 */
const range = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, index) => index + start)

/**
 * How many pages either side of the current page do we display
 */
const EITHER_SIDE_OF_CURRENT = 1
/**
 * How large is the gap between page sections when we replace with a placeholder
 */
const MINIMUM_PLACEHOLDER_GAP = 2

const GAP_PLACEHOLDER = 'gap'
type GapPlaceholder = typeof GAP_PLACEHOLDER

type GetPageNumbersArgs = { currentPage: number; lastPage: number }

const getPageNumbers = ({
  currentPage,
  lastPage,
}: GetPageNumbersArgs): (number | GapPlaceholder)[] => {
  const firstPage = 1
  // edges of the range of pages around the current page to display
  const leftEdge = Math.max(currentPage - EITHER_SIDE_OF_CURRENT, firstPage)
  const rightEdge = Math.min(currentPage + EITHER_SIDE_OF_CURRENT, lastPage)

  // unique page numbers to display
  const displayedPages = new Set([
    firstPage,
    ...range(leftEdge, rightEdge),
    lastPage,
  ])

  return Array.from(displayedPages).flatMap(
    (pageNumber, index, pageNumbers) => {
      // Insert appropriate items in gaps between this page number and previous
      const previousPageNumber = pageNumbers[index - 1] ?? 0
      const gap = pageNumber - (previousPageNumber + 1)

      if (gap === 0) {
        return pageNumber
      }
      if (gap < MINIMUM_PLACEHOLDER_GAP) {
        // gap is smaller than the minimum so just fill it with actual page numbers
        return range(pageNumber - gap, pageNumber)
      }
      if (gap >= MINIMUM_PLACEHOLDER_GAP) {
        // gap is larger than the minimum so insert ellipsis placeholdeers
        return [GAP_PLACEHOLDER, pageNumber]
      }
      return pageNumber
    },
  )
}

const roundToNearestThousands = (value: number): number =>
  Math.floor(value / 1000) * 1000

type GetNumberWithThousandsSeparator = (
  amount: number | string,
  precision?: number,
) => string

/**
 * Format a number or numeric string to a locale-sensitive string.
 * @param amount A number or numeric string.
 * @param precision The number of digits to appear after the decimal point
 * after the value is converted to a number string with thousands separator(s).
 * When omited, no change will be applied to the formatted value.
 * @returns A locale-sensitive string of a given number.
 */
const getNumberWithThousandsSeparator: GetNumberWithThousandsSeparator = (
  amount,
  precision,
) => {
  const stringifiedValue = amount.toString()
  const decimalDigits = stringifiedValue.split('.')[1]?.length

  const value = Number(amount) || 0
  return value.toLocaleString('en-GB', {
    minimumFractionDigits: precision ?? decimalDigits,
    maximumFractionDigits: precision ?? decimalDigits,
  })
}

type PaginationSummaryProps = {
  currentPage: number
  pageSize: number
  resultsCount: number
  totalResults?: number
  resultsLabel: string
  className?: string
}
function PaginationSummary({
  currentPage,
  pageSize,
  resultsCount,
  totalResults,
  resultsLabel,
  className,
}: PaginationSummaryProps) {
  if (resultsCount === 0) return null
  const currentPageFirstItem = (currentPage - 1) * pageSize + 1
  const currentPageLastItem = Math.min(currentPage * pageSize, resultsCount)
  const totalResultsText =
    totalResults && totalResults > resultsCount
      ? `${getNumberWithThousandsSeparator(roundToNearestThousands(resultsCount))}+`
      : resultsCount
  return (
    <p className={clsx('text-sm', className)}>
      Showing <span className="font-medium">{currentPageFirstItem}</span> to{' '}
      <span className="font-medium">{currentPageLastItem}</span> of{' '}
      <span className="font-medium">{totalResultsText}</span> {resultsLabel}
    </p>
  )
}

type PaginationNavProps = { children: ReactNode }
function PaginationNav({ children }: PaginationNavProps) {
  return (
    <nav
      className="isolate inline-flex -space-x-px rounded-md shadow-xs"
      aria-label="Pagination"
    >
      {children}
    </nav>
  )
}

type PaginationItemProps = {
  children: ReactNode
  current?: boolean
  enabled?: boolean
} & AnchorHTMLAttributes<HTMLAnchorElement>

const PaginationItem = forwardRef<HTMLAnchorElement, PaginationItemProps>(
  function PaginationItem(
    { current, children, className, enabled, href, ...props },
    ref,
  ) {
    const Component = enabled ? 'a' : 'span'

    return (
      <Component
        href={href}
        ref={ref}
        aria-current={current ? 'page' : undefined}
        className={clsx(
          'relative inline-grid h-8 min-w-8 place-content-center border px-4 font-medium first:rounded-l-md first:px-2 last:rounded-r-md last:px-2 focus-visible:z-20',
          {
            'border-primary-500 bg-primary-50 text-primary-600 z-10 text-sm':
              current,
            'border-gray-300 text-xs text-gray-500 hover:bg-gray-50': !current,
          },
          className,
        )}
        {...props}
      >
        {children}
      </Component>
    )
  },
)

type PaginationLinkProps = {
  pageNumber: number | null
  hash?: string
  children: ReactNode
}

function getPaginationHref(
  asPath: string,
  pageNumber: number | null,
  hash?: string,
): string {
  const [pathAndQuery = asPath] = asPath.split('#')
  const [path, queryString = ''] = pathAndQuery.split('?')
  const params = new URLSearchParams(queryString)

  if (pageNumber === null) {
    params.delete('page')
  } else {
    params.set('page', String(pageNumber))
  }

  const nextQueryString = params.toString()
  return `${path}${nextQueryString ? `?${nextQueryString}` : ''}${hash ? `#${hash}` : ''}`
}

function NextPaginationLink({
  pageNumber,
  hash,
  children,
}: PaginationLinkProps) {
  const { pathname, query } = useRouter()
  const href = { pathname, query: { ...query, page: pageNumber }, hash }

  return (
    <NextLink href={href} passHref legacyBehavior>
      {children}
    </NextLink>
  )
}

function MobilePaginationLink({
  pageNumber,
  hash,
  children,
  className,
}: PaginationLinkProps & { className?: string }) {
  const { asPath } = useRouter()
  const href = getPaginationHref(asPath, pageNumber, hash)

  return (
    <Anchor href={href} variant="outlined" size="medium" className={className}>
      {children}
    </Anchor>
  )
}
type PaginationItemType = 'previous' | 'next' | 'page' | 'gap'
type PaginationRangeItem = {
  key: string | number
  enabled: boolean
  pageNumber: number | null
  type: PaginationItemType
  ariaLabel: string | undefined
  visibleLabel: ReactNode
}

const getPaginationRange = (currentPage: number, lastPage: number) => {
  const pageNumbers = getPageNumbers({ currentPage, lastPage })
  const previousEnabled = currentPage > 1
  const nextEnabled = currentPage < lastPage
  const previous: PaginationRangeItem = {
    key: 'previous',
    type: 'previous',
    enabled: previousEnabled,
    pageNumber: previousEnabled ? currentPage - 1 : null,
    ariaLabel: 'previous',
    visibleLabel: <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />,
  }
  const next: PaginationRangeItem = {
    type: 'next',
    key: 'next',
    enabled: nextEnabled,
    pageNumber: nextEnabled ? currentPage + 1 : null,
    ariaLabel: 'next',
    visibleLabel: <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />,
  }
  return {
    range: [
      previous,
      ...pageNumbers.map((pageNumber, index): PaginationRangeItem =>
        typeof pageNumber === 'number'
          ? {
              key: pageNumber,
              type: 'page',
              enabled: true,
              pageNumber,
              ariaLabel: `Page ${pageNumber}`,
              visibleLabel: pageNumber,
            }
          : {
              key: `gap-${index}`,
              type: 'gap',
              enabled: false,
              pageNumber: null,
              ariaLabel: undefined,
              visibleLabel: '…',
            },
      ),
      next,
    ],
    previousEnabled,
    nextEnabled,
  }
}

type Props = {
  currentPage: number
  resultsCount: number
  pageSize: number
  totalResults?: number
  resultsLabel?: string
  hash?: string
  Link?: typeof NextPaginationLink
  className?: string
}

export function PaginationDeprecated({
  currentPage,
  resultsCount,
  pageSize,
  totalResults,
  resultsLabel = 'results',
  hash,
  Link = NextPaginationLink,
  className,
}: Props): ReactNode {
  const lastPage = Math.ceil(resultsCount / pageSize)
  const showNavigation = lastPage > 1
  const { range, previousEnabled, nextEnabled } = getPaginationRange(
    currentPage,
    lastPage,
  )

  return (
    <div
      className={cn(
        'flex flex-1 flex-col flex-wrap justify-between gap-4 sm:flex-row sm:items-center',
        className,
      )}
    >
      <PaginationSummary
        currentPage={currentPage}
        pageSize={pageSize}
        resultsCount={resultsCount}
        totalResults={totalResults}
        resultsLabel={resultsLabel}
      />
      {showNavigation ? (
        <div className="ml-auto hidden sm:block">
          <PaginationNav>
            {range.map(
              ({ key, pageNumber, ariaLabel, visibleLabel, enabled }) => {
                const item = (
                  <PaginationItem
                    key={key}
                    enabled={enabled}
                    aria-label={ariaLabel}
                    current={pageNumber === currentPage}
                  >
                    {visibleLabel}
                  </PaginationItem>
                )
                return enabled ? (
                  <Link key={key} pageNumber={pageNumber} hash={hash}>
                    {item}
                  </Link>
                ) : (
                  item
                )
              },
            )}
          </PaginationNav>
        </div>
      ) : null}
      <div className="flex grow justify-between sm:hidden">
        {previousEnabled ? (
          <MobilePaginationLink pageNumber={currentPage - 1} hash={hash}>
            Previous
          </MobilePaginationLink>
        ) : null}
        {nextEnabled ? (
          <MobilePaginationLink
            pageNumber={currentPage + 1}
            hash={hash}
            className="ml-auto"
          >
            Next
          </MobilePaginationLink>
        ) : null}
      </div>
    </div>
  )
}
